import { REDUCTION_DIFFERENCE_THRESHOLD } from 'common/constants/general';
import { drawScopedKey } from '../../magic-report/sections/draw-scoped-sections.util';

interface ReductionGateStatus {
  requiresManualComment: boolean;
  hasManualComment: boolean;
  blockMessage: string | undefined;
  /** Draws still missing a required manual reduction comment, for naming them in the UI. */
  offendingDrawNames: string[];
}

/** Evaluates one draw's reduction section against that draw's own budget difference. */
const evaluateDraw = (
  reductionSection: any,
  difference: number,
  isMagicReport: boolean,
): { requiresManualComment: boolean; hasManualComment: boolean } => {
  const rowsField = reductionSection?.fields?.find((f: any) => f.fieldName === 'ReductionCommentsRows');
  const hasAutoReductionRows = ((rowsField?.entries as any[]) || []).some((e: any) => {
    try {
      const p = JSON.parse(e.value || '');
      return p && typeof p === 'object';
    } catch {
      return false;
    }
  });

  const requiresManualComment =
    isMagicReport && difference < 0 && Math.abs(difference) >= REDUCTION_DIFFERENCE_THRESHOLD && !hasAutoReductionRows;

  const zeroWorkField = reductionSection?.fields?.find((f: any) => f.fieldName === 'ZeroWorkComments');
  const partialField = reductionSection?.fields?.find((f: any) => f.fieldName === 'PartialReductionComments');
  const commentsField = reductionSection?.fields?.find((f: any) => f.fieldName === 'Comments');
  const hasManualComment =
    !!(zeroWorkField?.value || '').trim() ||
    !!(partialField?.value || '').trim() ||
    ((commentsField?.entries as any[]) || []).some((e: any) => (e.value || '').trim());

  return { requiresManualComment, hasManualComment };
};

/**
 * Whether a manual reduction comment is still required before the reviewer can proceed.
 *
 * With several draws covered, each is judged on its own reduction section and its own budget
 * difference; the gate blocks while ANY draw needs a comment it doesn't have.
 *
 * Pass `drawId` to evaluate just that draw (used by each draw's tab for its own alert).
 */
export const useReductionGateStatus = (reportState: any, isMagicReport: boolean, drawId?: string): ReductionGateStatus => {
  const sections = reportState?.sections;
  const draws = reportState?.draws as Array<{ drawId: string; drawSummary?: { name?: string }; budgetSummary?: any }> | undefined;

  const drawsToCheck = drawId ? (draws || []).filter(d => d.drawId === drawId) : draws;

  // No draw list (legacy single-draw state): fall back to the unscoped section and top-level budget.
  if (!drawsToCheck || drawsToCheck.length === 0) {
    const result = evaluateDraw(sections?.['reduction-comments'], Number(reportState?.budgetSummary?.difference ?? 0), isMagicReport);
    const blocked = result.requiresManualComment && !result.hasManualComment;
    return {
      ...result,
      offendingDrawNames: [],
      blockMessage: blocked ? 'A manual reduction comment is required in the Reduction Comments section before proceeding.' : undefined,
    };
  }

  const perDraw = drawsToCheck.map(draw => ({
    name: draw.drawSummary?.name || 'Draw',
    ...evaluateDraw(
      sections?.[drawScopedKey('reduction-comments', draw.drawId)],
      Number(draw.budgetSummary?.difference ?? 0),
      isMagicReport,
    ),
  }));

  const offending = perDraw.filter(d => d.requiresManualComment && !d.hasManualComment);
  const requiresManualComment = perDraw.some(d => d.requiresManualComment);
  // "Has" only counts when nothing is still outstanding, so callers can keep using
  // `requiresManualComment && !hasManualComment` as the blocked check.
  const hasManualComment = requiresManualComment && offending.length === 0;

  const offendingDrawNames = offending.map(d => d.name);
  const blockMessage =
    offending.length > 0
      ? offendingDrawNames.length > 1
        ? `A manual reduction comment is required for ${offendingDrawNames.join(', ')} before proceeding.`
        : 'A manual reduction comment is required in the Reduction Comments section before proceeding.'
      : undefined;

  return { requiresManualComment, hasManualComment, blockMessage, offendingDrawNames };
};
